import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/contexts/SidebarContext";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ChevronLeft, ChevronRight, Store } from "lucide-react";
import { navigationSections } from "./navigation-sections";
import { SidebarNavItem } from "./SidebarNavItem";

interface SidebarProps {
  activePath?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ activePath = "/" }) => {
  const { collapsed, toggleSidebar } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["catalog"]);

  const toggleSubmenu = (key: string) => {
    setExpandedMenus((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  return (
    <Tooltip.Provider delayDuration={100}>
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed left-0 top-0 h-screen z-30",
          "bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800",
          "flex flex-col sidebar-transition",
          collapsed ? "w-16" : "w-64",
        )}
      >
        {/* Logo Section */}
        <div
          className={cn(
            "h-16 flex items-center border-b border-slate-200 dark:border-slate-800",
            collapsed ? "justify-center px-2" : "px-4",
          )}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
              <Store className="w-4 h-4 text-white" />
            </div>
            {!collapsed && (
              <span
                data-testid="sidebar-logo-text"
                className="font-heading font-bold text-lg text-slate-900 dark:text-slate-100"
              >
                SmartStore
              </span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navigationSections.map((section, sectionIndex) => (
            <div key={section.title} className={cn(sectionIndex > 0 && "mt-6")}>
              {!collapsed && (
                <h3 className="px-4 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {section.title}
                </h3>
              )}
              {collapsed && sectionIndex > 0 && (
                <div className="mx-3 mb-2 border-t border-slate-200 dark:border-slate-700" />
              )}
              <ul className="space-y-1 px-2">
                {section.items.map((item) => (
                  <SidebarNavItem
                    key={item.key}
                    item={item}
                    collapsed={collapsed}
                    activePath={activePath}
                    expanded={expandedMenus.includes(item.key)}
                    onToggle={toggleSubmenu}
                  />
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-2 border-t border-slate-200 dark:border-slate-800">
          <button
            data-testid="sidebar-toggle-btn"
            onClick={toggleSidebar}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md",
              "text-slate-500 dark:text-slate-400",
              "hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300",
              "transition-colors duration-150",
              collapsed && "justify-center",
            )}
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            ) : (
              <>
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </Tooltip.Provider>
  );
};
